import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { X, Plus, Video, Phone, MapPin, Link as LinkIcon, UserPlus, AlertTriangle } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { splitPhonePrefill, isWebinarEventType, formatEventPrice } from '@/lib/scheduling/webinar';
import {
    buildCustomAnswers,
    validateCustomAnswer,
    type CustomAnswer,
    type CustomAnswerValue,
    type CustomQuestion,
} from '@/lib/scheduling/customQuestions';
import { COUNTRY_CODES as ALL_COUNTRY_CODES } from '@/lib/constants/countryCodes';
import { validatePhoneForCountry } from '@/lib/constants/phoneValidation';

interface BookingFormProps {
    onSubmit: (data: BookingFormData) => void;
    loading?: boolean;
    customQuestions?: CustomQuestion[];
    eventType?: any;
    showWhatsAppPhone?: boolean;
    prefill?: { name?: string; email?: string; phone?: string };
}

export interface BookingFormData {
    invitee_name: string;
    invitee_email: string;
    invitee_phone: string;
    guest_emails?: string[];
    location_type?: string;
    location_details?: string;
    notes?: string;
    answers?: CustomAnswer[];
}

// Every dial code once (several countries share +1, +7, …) — for prefill splitting.
const COUNTRY_CODES = [...new Set(ALL_COUNTRY_CODES.map((c) => c.code))];

const buildPhone = (code: string, number: string) => {
    const digits = number.replace(/[^\d]/g, '');
    return digits ? `${code}${digits}` : '';
};

export function BookingForm({ onSubmit, loading, customQuestions = [], eventType, showWhatsAppPhone = false, prefill }: BookingFormProps) {
    const normalizedLocations = (() => {
        let locs = eventType?.location_options;
        if (typeof locs === 'string') { try { locs = JSON.parse(locs); } catch { locs = []; } }
        if (!Array.isArray(locs) || locs.length === 0) locs = [{ type: 'google_meet', details: null }];
        return locs;
    })();

    const showLocationChooser = normalizedLocations.length > 1;
    const defaultLocationType = normalizedLocations[0]?.type || 'google_meet';

    const isWebinar = isWebinarEventType(eventType);
    const priceLabel = isWebinar ? formatEventPrice(eventType?.price, eventType?.currency) : null;

    const [formData, setFormData] = useState<BookingFormData>(() => ({
        invitee_name: prefill?.name || '',
        invitee_email: prefill?.email || '',
        invitee_phone: '',
        guest_emails: [],
        location_type: defaultLocationType,
        location_details: '',
        notes: '',
        answers: [],
    }));
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [guestInputs, setGuestInputs] = useState<string[]>([]);
    const [showGuestSection, setShowGuestSection] = useState(false);

    const prefillPhone = splitPhonePrefill(prefill?.phone || '', COUNTRY_CODES);
    const [waCode, setWaCode] = useState(prefillPhone.code || '+91');
    const [waNum, setWaNum] = useState(prefillPhone.number);

    // Custom question answers, keyed by question id.
    const [customValues, setCustomValues] = useState<Record<string, CustomAnswerValue>>({});
    const [customPhoneCodes, setCustomPhoneCodes] = useState<Record<string, string>>({});

    const setCustomValue = (id: string, value: CustomAnswerValue) => {
        setCustomValues((p) => ({ ...p, [id]: value }));
        const key = `custom_${id}`;
        if (errors[key]) setErrors((p) => { const e = { ...p }; delete e[key]; return e; });
    };

    const toggleCustomOption = (id: string, option: string, checked: boolean) => {
        const current = Array.isArray(customValues[id]) ? (customValues[id] as string[]) : [];
        setCustomValue(id, checked ? [...current, option] : current.filter((o) => o !== option));
    };

    const selectedLocation = normalizedLocations.find((l: any) => l.type === formData.location_type);

    useEffect(() => {
        if (!showLocationChooser && normalizedLocations.length === 1) {
            const sole = normalizedLocations[0];
            if (sole?.details && sole.type !== 'phone' && !formData.location_details) {
                setFormData((p) => ({ ...p, location_details: sole.details }));
            }
        }
    }, []);

    const set = (field: keyof BookingFormData, value: string) => {
        setFormData((p) => ({ ...p, [field]: value }));
        if (errors[field]) setErrors((p) => { const e = { ...p }; delete e[field]; return e; });
    };

    const addGuestRow = () => {
        if (guestInputs.length < 10) setGuestInputs((p) => [...p, '']);
    };

    const updateGuestInput = (idx: number, value: string) => {
        setGuestInputs((p) => { const n = [...p]; n[idx] = value; return n; });
        const updated = [...guestInputs];
        updated[idx] = value;
        const validEmails = updated.filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim()));
        setFormData((p) => ({ ...p, guest_emails: validEmails }));
    };

    const removeGuestRow = (idx: number) => {
        setGuestInputs((p) => { const n = p.filter((_, i) => i !== idx); return n; });
        const updated = guestInputs.filter((_, i) => i !== idx);
        const validEmails = updated.filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim()));
        setFormData((p) => ({ ...p, guest_emails: validEmails }));
    };

    const validate = () => {
        const e: Record<string, string> = {};
        if (!formData.invitee_name.trim()) e.invitee_name = 'Name is required';
        if (!formData.invitee_email.trim()) e.invitee_email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.invitee_email)) e.invitee_email = 'Enter a valid email';
        const phoneError = validatePhoneForCountry(waCode, waNum);
        if (phoneError) e.invitee_phone = phoneError;
        if (formData.location_type === 'in_person' && !formData.location_details?.trim()) e.location_details = 'Address is required';
        if (formData.location_type === 'custom' && !formData.location_details?.trim()) e.location_details = 'Meeting link is required';
        customQuestions.forEach((q) => {
            const error = validateCustomAnswer(q, customValues[q.id], customPhoneCodes[q.id] || '+91');
            if (error) e[`custom_${q.id}`] = error;
        });
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        const payload = { ...formData };
        const waPhone = buildPhone(waCode, waNum);
        if (formData.location_type === 'phone') payload.location_details = waPhone;
        payload.invitee_phone = waPhone;
        payload.answers = buildCustomAnswers(customQuestions, customValues, customPhoneCodes);
        onSubmit(payload);
    };

    const locationIcon = (type: string) => {
        if (type === 'google_meet') return <Video className="h-4 w-4 text-blue-500" />;
        if (type === 'zoom') return <Video className="h-4 w-4 text-[#2D8CFF]" />;
        if (type === 'phone') return <Phone className="h-4 w-4 text-green-500" />;
        if (type === 'in_person') return <MapPin className="h-4 w-4 text-red-500" />;
        return <LinkIcon className="h-4 w-4 text-purple-500" />;
    };

    const locationName = (type: string) => {
        if (type === 'google_meet') return 'Google Meet';
        if (type === 'zoom') return 'Zoom';
        if (type === 'phone') return 'Phone Call';
        if (type === 'in_person') return 'In Person';
        if (type === 'custom') return 'Custom Link';
        return type;
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
                <Label htmlFor="name" className="text-sm font-medium">
                    Your name <span className="text-destructive">*</span>
                </Label>
                <Input
                    id="name"
                    value={formData.invitee_name}
                    onChange={(e) => set('invitee_name', e.target.value)}
                    placeholder="John Doe"
                    disabled={loading}
                    className={errors.invitee_name ? 'border-destructive' : ''}
                />
                {errors.invitee_name && <p className="text-xs text-destructive">{errors.invitee_name}</p>}
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium">
                    Email address <span className="text-destructive">*</span>
                </Label>
                <Input
                    id="email"
                    type="email"
                    value={formData.invitee_email}
                    onChange={(e) => set('invitee_email', e.target.value)}
                    placeholder="you@example.com"
                    disabled={loading}
                    className={errors.invitee_email ? 'border-destructive' : ''}
                />
                {errors.invitee_email && <p className="text-xs text-destructive">{errors.invitee_email}</p>}
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="wa-phone" className="text-sm font-medium flex items-center gap-1.5">
                    {showWhatsAppPhone && (
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-green-500" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.553 4.122 1.522 5.857L.057 23.885l6.196-1.623A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.885 0-3.65-.487-5.188-1.342l-.372-.22-3.678.964.981-3.584-.242-.38A9.944 9.944 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
                        </svg>
                    )}
                    Phone number <span className="text-destructive">*</span>
                </Label>
                <div className="flex gap-2">
                    <select
                        className="h-10 w-28 rounded-md border border-input bg-background px-2 text-sm"
                        aria-label="Country code"
                        value={waCode}
                        onChange={(e) => setWaCode(e.target.value)}
                        disabled={loading}
                    >
                        {ALL_COUNTRY_CODES.map((c, i) => (
                            <option key={`${c.code}-${i}`} value={c.code}>{c.flag} {c.code}</option>
                        ))}
                    </select>
                    <Input
                        id="wa-phone"
                        type="tel"
                        value={waNum}
                        onChange={(e) => {
                            setWaNum(e.target.value);
                            if (errors.invitee_phone) setErrors((p) => { const x = { ...p }; delete x.invitee_phone; return x; });
                        }}
                        placeholder="9876543210"
                        disabled={loading}
                        className={errors.invitee_phone ? 'border-destructive' : ''}
                    />
                </div>
                {errors.invitee_phone && <p className="text-xs text-destructive">{errors.invitee_phone}</p>}
                {!errors.invitee_phone && formData.location_type === 'phone' && (
                    <p className="text-xs text-muted-foreground">We'll call you on this number for the meeting</p>
                )}
                {!errors.invitee_phone && formData.location_type !== 'phone' && showWhatsAppPhone && (
                    <p className="text-xs text-muted-foreground">You'll get a WhatsApp confirmation and reminder</p>
                )}
            </div>

            {showLocationChooser && (
                <div className="space-y-2">
                    <Label className="text-sm font-medium">Location</Label>
                    <RadioGroup
                        value={formData.location_type}
                        onValueChange={(v) => {
                            set('location_type', v);
                            const loc = normalizedLocations.find((l: any) => l.type === v);
                            set('location_details', loc?.details && v !== 'google_meet' && v !== 'phone' ? loc.details : '');
                        }}
                        disabled={loading}
                        className="space-y-1"
                    >
                        {normalizedLocations.map((loc: any) => (
                            <div key={loc.type} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                                <RadioGroupItem value={loc.type} id={loc.type} />
                                <Label htmlFor={loc.type} className="flex items-center gap-2 cursor-pointer">
                                    {locationIcon(loc.type)}
                                    <span className="text-sm font-medium">{locationName(loc.type)}</span>
                                </Label>
                            </div>
                        ))}
                    </RadioGroup>
                </div>
            )}

            {formData.location_type === 'in_person' && !selectedLocation?.details && (
                <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Meeting address <span className="text-destructive">*</span></Label>
                    <Input value={formData.location_details || ''} onChange={(e) => set('location_details', e.target.value)} placeholder="123 Main St, City" disabled={loading} />
                    {errors.location_details && <p className="text-xs text-destructive">{errors.location_details}</p>}
                </div>
            )}

            {formData.location_type === 'custom' && !selectedLocation?.details && (
                <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Meeting link <span className="text-destructive">*</span></Label>
                    <Input type="url" value={formData.location_details || ''} onChange={(e) => set('location_details', e.target.value)} placeholder="https://zoom.us/j/..." disabled={loading} />
                    {errors.location_details && <p className="text-xs text-destructive">{errors.location_details}</p>}
                </div>
            )}

            {customQuestions.map((q) => {
                const fieldId = `custom-${q.id}`;
                const error = errors[`custom_${q.id}`];
                const text = typeof customValues[q.id] === 'string' ? (customValues[q.id] as string) : '';
                const picked = Array.isArray(customValues[q.id]) ? (customValues[q.id] as string[]) : [];
                const inputClass = error ? 'border-destructive' : '';
                return (
                    <div key={q.id} className="space-y-1.5" data-testid="booking-custom-question">
                        <Label htmlFor={fieldId} className="text-sm font-medium">
                            {q.question}{q.required && <span className="text-destructive"> *</span>}
                        </Label>

                        {q.type === 'textarea' && (
                            <Textarea id={fieldId} rows={3} value={text} placeholder={q.placeholder}
                                onChange={(e) => setCustomValue(q.id, e.target.value)}
                                disabled={loading} className={inputClass} />
                        )}

                        {(q.type === 'text' || q.type === 'email' || q.type === 'number' || q.type === 'date') && (
                            <Input id={fieldId} value={text} placeholder={q.placeholder}
                                type={q.type === 'text' ? 'text' : q.type}
                                inputMode={q.type === 'number' ? 'decimal' : undefined}
                                onChange={(e) => setCustomValue(q.id, e.target.value)}
                                disabled={loading} className={inputClass} />
                        )}

                        {q.type === 'phone' && (
                            <div className="flex gap-2">
                                <select
                                    aria-label={`${q.question} country code`}
                                    className="h-10 w-28 rounded-md border border-input bg-background px-2 text-sm"
                                    value={customPhoneCodes[q.id] || '+91'}
                                    onChange={(e) => setCustomPhoneCodes((p) => ({ ...p, [q.id]: e.target.value }))}
                                    disabled={loading}
                                >
                                    {ALL_COUNTRY_CODES.map((c, i) => (
                                        <option key={`${c.code}-${i}`} value={c.code}>{c.flag} {c.code}</option>
                                    ))}
                                </select>
                                <Input id={fieldId} type="tel" inputMode="numeric" value={text}
                                    placeholder={q.placeholder || '9876543210'}
                                    onChange={(e) => setCustomValue(q.id, e.target.value.replace(/\D/g, '').slice(0, 15))}
                                    disabled={loading} className={inputClass} />
                            </div>
                        )}

                        {q.type === 'select' && (
                            <select id={fieldId} value={text}
                                onChange={(e) => setCustomValue(q.id, e.target.value)}
                                disabled={loading}
                                className={`h-10 w-full rounded-md border border-input bg-background px-3 text-sm ${inputClass}`}
                            >
                                <option value="">Select an option</option>
                                {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                        )}

                        {q.type === 'radio' && (
                            <RadioGroup value={text} onValueChange={(v) => setCustomValue(q.id, v)} disabled={loading} className="gap-2">
                                {(q.options || []).map((o, i) => (
                                    <div key={o} className="flex items-center gap-2">
                                        <RadioGroupItem value={o} id={`${fieldId}-${i}`} />
                                        <Label htmlFor={`${fieldId}-${i}`} className="text-sm font-normal cursor-pointer">{o}</Label>
                                    </div>
                                ))}
                            </RadioGroup>
                        )}

                        {q.type === 'checkbox' && (
                            <div className="space-y-2">
                                {(q.options || []).map((o, i) => (
                                    <div key={o} className="flex items-center gap-2">
                                        <Checkbox id={`${fieldId}-${i}`} checked={picked.includes(o)} disabled={loading}
                                            onCheckedChange={(checked) => toggleCustomOption(q.id, o, checked === true)} />
                                        <Label htmlFor={`${fieldId}-${i}`} className="text-sm font-normal cursor-pointer">{o}</Label>
                                    </div>
                                ))}
                            </div>
                        )}

                        {error && <p className="text-xs text-destructive">{error}</p>}
                    </div>
                );
            })}

            <div className="space-y-1.5">
                <Label htmlFor="notes" className="text-sm font-medium">Additional notes <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => set('notes', e.target.value)}
                    placeholder="Please share anything that will help prepare for our meeting."
                    disabled={loading}
                    rows={3}
                />
            </div>

            {!isWebinar && (
            <div className="space-y-2">
                {!showGuestSection ? (
                    <button
                        type="button"
                        onClick={() => { setShowGuestSection(true); setGuestInputs(['']); }}
                        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        disabled={loading}
                    >
                        <UserPlus className="h-4 w-4" />
                        Add guests
                    </button>
                ) : (
                    <div className="space-y-2">
                        <Label className="text-sm font-medium">Add guests</Label>
                        {guestInputs.map((val, idx) => (
                            <div key={idx} className="relative flex items-center">
                                <Input
                                    type="email"
                                    value={val}
                                    onChange={(e) => updateGuestInput(idx, e.target.value)}
                                    placeholder="Email"
                                    disabled={loading}
                                    className="pr-9"
                                />
                                <button
                                    type="button"
                                    onClick={() => removeGuestRow(idx)}
                                    disabled={loading}
                                    className="absolute right-2.5 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                        {guestInputs.length < 10 && (
                            <button
                                type="button"
                                onClick={addGuestRow}
                                disabled={loading}
                                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors pt-1"
                            >
                                <Plus className="h-4 w-4" />
                                Add another
                            </button>
                        )}
                    </div>
                )}
            </div>
            )}

            {priceLabel && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5" data-testid="paid-no-refund-warning">
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800">
                        The registration fee of <strong>{priceLabel}</strong> is non-refundable once paid.
                        You'll be redirected to a secure Razorpay page — your seat is confirmed after payment.
                    </p>
                </div>
            )}

            <Button type="submit" className="w-full" size="lg" disabled={loading}>
                {loading
                    ? 'Confirming...'
                    : priceLabel
                        ? `Register & Pay ${priceLabel}`
                        : isWebinar ? 'Register' : 'Confirm'}
            </Button>
        </form>
    );
}
